import { gql, NetworkStatus, useQuery } from '@apollo/client'

import { useGetNewestData } from '@/clients/utoniq-core'
import { currentArtistVar } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  ArtistSmallDetailFragmentDoc,
  ArtistThreadChannelConnectionFragmentDoc,
  ArtistThreadChannelFilterType,
  ArtistThreadChannelOrderByType,
  ArtistThreadChannelsOptionInput,
  ArtistThreadsForGuestQuery,
  ArtistThreadsForGuestQueryVariables,
  ArtistThreadsQuery,
  ArtistThreadsQueryVariables,
  CurrentUserFragmentDoc,
  FetchNewArtistThreadsQuery,
  FetchNewArtistThreadsQueryVariables,
  ThreadBoxFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'
export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{ artistId: string }>()
  const { user, userClaims } = useCurrentUser()
  const artistId = router.query.artistId ?? config.artistId
  const params = new URLSearchParams(window.location.search)
  const sort = params.get('sort')
  const tab = params.get('tab')

  const machingFilter =
    Object.values(ArtistThreadChannelFilterType).find(value => value === tab) ??
    undefined

  const machingSort =
    Object.values(ArtistThreadChannelOrderByType).find(
      value => value === sort
    ) ?? ArtistThreadChannelOrderByType.CreatedAt

  const eventFilter: ArtistThreadChannelsOptionInput = {
    filter: machingFilter as ArtistThreadChannelFilterType,
    orderBy: machingSort as ArtistThreadChannelOrderByType,
  }

  const { isFirstLoadRef } = useGetNewestData<
    FetchNewArtistThreadsQuery,
    FetchNewArtistThreadsQueryVariables
  >({
    query: FETCH_NEW_THREAD,
    fields: [
      { fieldName: 'artistThreadChannels', fragment: ThreadBoxFragmentDoc },
    ],
    variables: {
      inputs: {
        artistId,
        options: {
          filter: machingFilter as ArtistThreadChannelFilterType,
          orderBy: machingSort as ArtistThreadChannelOrderByType,
        },
      },
      pageInput: { limit: 30 },
      artistId,
    },
    onCompleted(data) {
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })

  const { data, fetchMore, refetch, networkStatus } = useQuery<
    ArtistThreadsQuery,
    ArtistThreadsQueryVariables
  >(QUERY, {
    variables: {
      inputs: {
        artistId,
        options: {
          orderBy: machingSort as ArtistThreadChannelOrderByType,
          filter: machingFilter as ArtistThreadChannelFilterType,
        },
      },
      artistId,
    },
    skip: !user?.isSigned,
    fetchPolicy: 'cache-and-network',
    // Get cache first in second time
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    // - On the first time: data has not cached,
    //      should call api first -> onCompleted run after useEffect with isFirstLoadRef = true (getNewThreads)
    // - From second time: data has been cached,
    //      no need to call api -> onCompleted run before useEffect with isFirstLoadRef = false (getNewThreads)
    onCompleted: data => {
      isFirstLoadRef.current = false
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })
  const isNextFetch = !!data

  const {
    data: dataForGuest,
    networkStatus: networkStatusForGuest,
    fetchMore: fetchMoreForGuest,
    refetch: refetchForGuest,
  } = useQuery<ArtistThreadsForGuestQuery, ArtistThreadsForGuestQueryVariables>(
    QUERY_FOR_GUEST,
    {
      variables: {
        inputs: {
          artistId,
          options: {
            orderBy: machingSort as ArtistThreadChannelOrderByType,
            filter: machingFilter as ArtistThreadChannelFilterType,
          },
        },
        artistId,
      },
      skip: !!user?.isSigned,
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      onCompleted(data) {
        isFirstLoadRef.current = false
        if (data.artist) {
          currentArtistVar(data.artist)
        }
      },
    }
  )
  const isNextFetchForGuest = !!dataForGuest

  return {
    state: {
      loading:
        (networkStatus === NetworkStatus.loading && !isNextFetch) ||
        (networkStatusForGuest === NetworkStatus.loading &&
          !isNextFetchForGuest),
      refetching:
        networkStatus === NetworkStatus.fetchMore ||
        networkStatusForGuest === NetworkStatus.fetchMore,
      hasNextPage: !!(data ?? dataForGuest)?.artistThreadChannels?.pageInfo
        .hasNextPage,
      artistId,
      artistName:
        data?.artist?.displayName ?? dataForGuest?.artist?.displayName ?? '',
      hasPatron: !!data?.artist?.patron?.id,
      isShowModalPersonalInfoRequire: !!(
        data?.user?.isMembershipPersonalInformationRequired &&
        data?.artist?.patron?.isPersonalInformationRequired
      ),
      threads:
        data?.artistThreadChannels ??
        dataForGuest?.artistThreadChannels ??
        null,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      followArtists: data?.user?.organizationFollowees ?? null,
      memberArtists: data?.user?.organizationPatrons ?? null,
      isLogin: user ? true : false,
      user: data?.user ?? null,
      liveStreamId: (data ?? dataForGuest)?.artist?.currentLive?.id,
      filter: eventFilter,
    },
    actions: {
      async onFetchMore(options) {
        if (!user) {
          await fetchMoreForGuest({
            variables: {
              inputs: {
                artistId,
                options,
              },
              pageInput: {
                cursor: dataForGuest?.artistThreadChannels?.pageInfo.endCursor,
              },
            },
          })
          return
        }
        await fetchMore({
          variables: {
            inputs: {
              artistId,
              options,
            },
            pageInput: {
              cursor: data?.artistThreadChannels?.pageInfo.endCursor,
            },
          },
        })
      },
      async onRefetch(options) {
        if (!user) {
          await refetchForGuest({
            inputs: {
              artistId,
              options,
            },
          })
          return
        }
        await refetch({
          inputs: {
            artistId,
            options,
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query ArtistThreads(
    $inputs: ArtistThreadChannelsInput!
    $pageInput: PageInput
    $artistId: ID
  ) {
    artistThreadChannels(inputs: $inputs, pageInput: $pageInput) {
      ...ArtistThreadChannelConnection
    }
    artist(id: $artistId) {
      ...ArtistBottomTab
      id
      displayName
      accountId
      formattedAccountId @client
      patron {
        id
        isPersonalInformationRequired
      }
      currentLive {
        id
      }
    }
    user {
      ...CurrentUser
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${ArtistThreadChannelConnectionFragmentDoc}
  ${CurrentUserFragmentDoc}
`

const FETCH_NEW_THREAD = gql`
  query FetchNewArtistThreads(
    $inputs: ArtistThreadChannelsInput!
    $pageInput: PageInput
    $artistId: ID
  ) {
    artist(id: $artistId) {
      ...ArtistBottomTab
      id
      displayName
      accountId
      formattedAccountId @client
      patron {
        id
        isPersonalInformationRequired
      }
      currentLive {
        id
      }
    }
    artistThreadChannels(inputs: $inputs, pageInput: $pageInput) {
      ...ArtistThreadChannelConnection
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${ArtistThreadChannelConnectionFragmentDoc}
`

const QUERY_FOR_GUEST = gql`
  query ArtistThreadsForGuest(
    $inputs: ArtistThreadChannelsInput!
    $pageInput: PageInput
    $artistId: ID
  ) {
    artistThreadChannels(inputs: $inputs, pageInput: $pageInput) {
      ...ArtistThreadChannelConnection
    }
    artist(id: $artistId) {
      ...ArtistBottomTab
      ...ArtistSmallDetail
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${ArtistThreadChannelConnectionFragmentDoc}
  ${ArtistSmallDetailFragmentDoc}
`
